import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import CodeMirror from "@uiw/react-codemirror";
import { type EditedRange } from "@/utils/applySearchReplaceBlocks";
import { html as htmlLang } from "@codemirror/lang-html";
import { Decoration, EditorView } from "@codemirror/view";
import { Button } from "@/components/ui/button";
import { ExternalLink, Loader2, Save } from "lucide-react";
import { getPageBaseUrl } from "./pageEditorUtils";

function createEditedLinesDecoration(doc: string, ranges: EditedRange[]) {
  const lineStarts: number[] = [0];
  for (let i = 0; i < doc.length; i++) {
    if (doc[i] === "\n") lineStarts.push(i + 1);
  }
  const lineDecoration = Decoration.line({ class: "cm-edited-line" });
  const decos: ReturnType<Decoration["range"]>[] = [];
  for (const { from, to } of ranges) {
    for (let i = 0; i < lineStarts.length; i++) {
      const lineStart = lineStarts[i];
      const lineEnd = i < lineStarts.length - 1 ? lineStarts[i + 1] - 1 : doc.length;
      if (from <= lineEnd && to >= lineStart) {
        decos.push(lineDecoration.range(lineStart, lineStart));
      }
    }
  }
  return Decoration.set(decos, true);
}

const editedLinesTheme = EditorView.baseTheme({
  ".cm-edited-line": { backgroundColor: "rgba(34, 197, 94, 0.15)" },
});

interface CodeTabProps {
  html: string;
  onHtmlChange: (html: string) => void;
  onSave?: () => void;
  isSaving?: boolean;
  hasUnsavedChanges?: boolean;
  route?: string;
}

export function CodeTab({ html, onHtmlChange, onSave, isSaving, hasUnsavedChanges, route }: CodeTabProps) {
  const [customDomainHostname, setCustomDomainHostname] = useState<string | null>(null);
  const [editedRanges, setEditedRanges] = useState<EditedRange[] | null>(null);

  useEffect(() => {
    fetch("/api/custom-domains")
      .then((res) => res.json())
      .then((data) => {
        const domains = data.customDomains || [];
        setCustomDomainHostname(domains[0]?.hostname ?? null);
      })
      .catch(() => {});
  }, []);

  const onChange = useCallback(
    (value: string) => {
      setEditedRanges(null);
      onHtmlChange(value);
    },
    [onHtmlChange]
  );

  const handleSave = useCallback(() => {
    setEditedRanges(null);
    onSave?.();
    toast.success("Saved");
  }, [onSave]);

  const editedLinesExtension = useMemo(() => {
    if (!editedRanges?.length) return [];
    return [
      editedLinesTheme,
      EditorView.decorations.of(createEditedLinesDecoration(html, editedRanges)),
    ];
  }, [editedRanges, html]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (onSave && !isSaving) {
          handleSave();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onSave, isSaving, handleSave]);

  return (
    <div className="flex-1 min-h-0 overflow-hidden relative min-w-0">
      <CodeMirror
        value={html}
        height="100%"
        extensions={[htmlLang(), EditorView.lineWrapping, ...editedLinesExtension]}
        onChange={onChange}
        basicSetup={{
          lineNumbers: true,
          foldGutter: true,
          highlightActiveLine: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: true,
        }}
        className="h-full text-sm"
      />
      <div className="absolute top-2 right-2 z-10 flex gap-1">
        {route !== undefined && (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            asChild
            className="h-8 w-8"
            title="Open page in new tab"
          >
            <a
              href={`${getPageBaseUrl(customDomainHostname)}${route || "/"}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        )}
        {hasUnsavedChanges && onSave && (
          <Button
            type="button"
            size="icon"
            onClick={handleSave}
            disabled={isSaving}
            className="shadow-lg"
            title="Save"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
